import { useEffect, useRef, useState } from 'react'
import { usePage } from '@inertiajs/react'
import { FileButton, Loader, LoadingOverlay, Tooltip } from '@mantine/core'
import {
  IconCheck, IconCrop, IconExclamationMark, IconFocus2, IconPhotoOff, IconPhotoPlus, IconTrash,
} from '@tabler/icons-react'
import { useTranslation } from 'react-i18next'
import StockPhotoTab from '../../../Partials/Studio/AdBuilder/Form/StockPhotoTab'
import { ImageCropDialog } from '../assets/ImageCropDialog'
import { FocusPointPicker } from '../forms/FocusPointPicker'
import { Segmented } from '../v2/Segmented'
import { useImageMediaField } from '../../fields/useImageMediaField'
import { proxiedImageUrl, useAdvertiserLibrary } from '../../fields/useAdvertiserLibrary'
import { fieldLabel } from '../../utils/uiLayoutV2'
import { RemoveTextButton, RemoveTextNotes, RemoveTextProgress, useRemoveText } from './RemoveText'
import classes from './V2Form.module.css'

/**
 * Remembers which library tile became the current image. Library pictures are imported as
 * new uploads, so the tile is matched by the source asset its click produced.
 */
export function usePickedTile(sourceAssetId) {
  const pending = useRef(null)
  const [picked, setPicked] = useState(null)
  useEffect(() => {
    if (pending.current && sourceAssetId) {
      setPicked({ url: pending.current, assetId: sourceAssetId })
      pending.current = null
    }
  }, [sourceAssetId])
  return {
    begin: (url) => { pending.current = url },
    // The import was refused: the next picture change is not this tile's.
    cancel: () => { pending.current = null },
    isPicked: (item, currentUrl) => Boolean(sourceAssetId) && (
      (item.assetId && item.assetId === sourceAssetId)
      || item.url === currentUrl
      || (picked?.assetId === sourceAssetId && picked?.url === item.url)
    ),
  }
}

/** A picture's remarks as an exclamation mark on the picture, read on hover (AD-252). */
export function ImageWarning({ messages }) {
  const shown = messages.filter(Boolean)
  if (!shown.length) return null
  return (
    <Tooltip
      label={shown.map((message) => <div key={message}>{message}</div>)}
      multiline
      w={260}
      withArrow
      events={{ hover: true, focus: true, touch: true }}
    >
      <span className={classes.imageWarning} role="img" tabIndex={0} aria-label={shown.join(' ')}>
        <IconExclamationMark size={16} stroke={2.4} />
      </span>
    </Tooltip>
  )
}

/**
 * Slide image (Figma "Media slides"): a 330×350 preview with Focus / Crop / Remove text / delete, or a
 * dashed drop area when empty; beside it "Advertiser Library" / "Stock Images" with 160×100
 * checkbox tiles, and the empty library state "No images found on the advertiser library"
 * with "Import Images from Website".
 */
export function ImagePanel(props) {
  const { t } = useTranslation()
  const { field, error, configuration, assetSourceUrls } = props
  const {
    media, target, accept, sourceUrl, previewUrl, cropMode, cropUseful, imageWarning,
    localError, busy, cropOpened, setCropOpened, chooseFile, adoptUrl, applyCrop, clear,
    focalPoint, focusUseful, setFocalPoint,
  } = useImageMediaField(props)
  const unsplashKey = usePage().props?.unsplashKey
  const [tab, setTab] = useState('library')
  const [focusOpened, setFocusOpened] = useState(false)
  const [over, setOver] = useState(false)
  const [stockQuery, setStockQuery] = useState('')
  const library = useAdvertiserLibrary({ configuration, assetSourceUrls, active: tab === 'library' })
  const picked = usePickedTile(media.sourceAssetId)
  const currentUrl = media.sourceAssetId ? configuration?.assets?.[media.sourceAssetId]?.url : null
  const removeText = useRemoveText({
    media, sourceUrl, onChange: props.onChange, adoptUrl, configuration, currentUrl,
  })
  // Photos only: a logo is placed whole and has no text to take off.
  const removeTextUseful = focusUseful && Boolean(sourceUrl)

  useEffect(() => { if (!media.sourceAssetId) setFocusOpened(false) }, [media.sourceAssetId])

  const drop = {
    onDragOver: (event) => { event.preventDefault(); setOver(true) },
    onDragLeave: () => setOver(false),
    onDrop: (event) => {
      event.preventDefault()
      setOver(false)
      const file = event.dataTransfer?.files?.[0]
      if (file) chooseFile(file)
    },
  }

  const adopt = (item, filename) => {
    picked.begin(item.url)
    adoptUrl(item.url, filename, item).then((imported) => { if (!imported) picked.cancel() })
  }

  return (
    <div>
      <div className={classes.media}>
        <div>
          <p className={classes.mediaLabel}>{fieldLabel(field)}</p>
          <div
            className={classes.mediaBox}
            data-empty={media.sourceAssetId ? undefined : 'true'}
            data-over={over ? 'true' : undefined}
            {...drop}
          >
            <LoadingOverlay visible={busy} zIndex={5} overlayProps={{ blur: 1 }} loaderProps={{ color: '#d00b65', size: 'sm' }} />
            <RemoveTextProgress removeText={removeText} />
            {media.sourceAssetId ? (
              <>
                <ImageWarning
                  messages={[
                    imageWarning,
                    removeText.cue && t('editorV2.form.image.textDetected'),
                  ]}
                />
                <div className={classes.mediaImage}>
                  {focusOpened && sourceUrl ? (
                    <FocusPointPicker src={sourceUrl} focalPoint={focalPoint} onPick={setFocalPoint} />
                  ) : focusUseful && sourceUrl ? (
                    // The whole picture and its point; the phone preview shows the crop.
                    <FocusPointPicker src={sourceUrl} focalPoint={focalPoint} readOnly />
                  ) : (
                    <img src={previewUrl || sourceUrl} alt="" />
                  )}
                </div>
                <div className={classes.mediaActions}>
                  <div>
                    {focusUseful && sourceUrl && (
                      <button
                        type="button"
                        className={classes.smallButton}
                        data-active={focusOpened ? 'true' : undefined}
                        onClick={() => setFocusOpened((open) => !open)}
                      >
                        <IconFocus2 size={14} stroke={1.8} />
                        {focusOpened ? t('editorV2.form.common.done') : t('editorV2.form.common.focus')}
                      </button>
                    )}
                    {cropUseful && sourceUrl && (
                      <button
                        type="button"
                        className={classes.smallButton}
                        onClick={() => { setFocusOpened(false); setCropOpened(true) }}
                      >
                        <IconCrop size={14} stroke={1.8} />
                        {t('editorV2.form.common.crop')}
                      </button>
                    )}
                    {removeTextUseful && <RemoveTextButton removeText={removeText} />}
                  </div>
                  <button
                    type="button"
                    className={classes.iconButton}
                    data-bordered="true"
                    aria-label={t('editorV2.form.common.removeLabel', { label: fieldLabel(field) })}
                    onClick={clear}
                  >
                    <IconTrash size={14} stroke={1.8} />
                  </button>
                </div>
              </>
            ) : (
              <FileButton accept={accept} onChange={chooseFile}>
                {(buttonProps) => (
                  <button {...buttonProps} type="button" className={classes.dropButton}>
                    <IconPhotoPlus size={24} stroke={1.6} />
                    <span>{t('editorV2.form.common.dropLine')}<br />{t('editorV2.form.common.dropFile')}</span>
                  </button>
                )}
              </FileButton>
            )}
          </div>
        </div>

        <div className={classes.sources}>
          <Segmented
            ariaLabel={t('editorV2.form.image.source')}
            value={tab}
            onChange={setTab}
            className={classes.tabsSegment}
            options={[
              { value: 'library', label: t('editorV2.form.common.advertiserLibrary') },
              { value: 'stock', label: t('editorV2.form.common.stockImages') },
            ]}
          />

          {tab === 'library' && (
            library.images.length > 0 || removeText.tiles.length > 0 ? (
              <div className={classes.sourceBox}>
                <div className={classes.tiles}>
                  {removeText.tiles.map((tile) => {
                    const selected = removeText.isCurrent(tile)
                    return (
                      <button
                        key={tile.key}
                        type="button"
                        className={classes.tile}
                        data-selected={selected ? 'true' : 'false'}
                        aria-pressed={selected}
                        onClick={() => { if (!selected) removeText.pickTile(tile) }}
                      >
                        <img src={tile.url} alt="" />
                        <span className={classes.tileCheck} aria-hidden="true">
                          {selected && <IconCheck size={12} stroke={3} />}
                        </span>
                        <span className={classes.tileTag} data-fresh={tile.key === 'result' ? 'true' : undefined}>
                          {tile.tag}
                        </span>
                      </button>
                    )
                  })}
                  {library.images.filter((item) => !removeText.tiles.some((tile) => tile.url === item.url)).map((item) => {
                    const selected = !removeText.tiles.some(removeText.isCurrent) && picked.isPicked(item, currentUrl)
                    return (
                      <button
                        key={item.url}
                        type="button"
                        className={classes.tile}
                        data-selected={selected ? 'true' : 'false'}
                        aria-pressed={selected}
                        title={item.url}
                        onClick={() => { if (!selected) adopt(item) }}
                      >
                        <img
                          src={proxiedImageUrl(item)}
                          alt=""
                          loading="lazy"
                          onError={(event) => { event.currentTarget.closest('button').hidden = true }}
                        />
                        <span className={classes.tileCheck} aria-hidden="true">
                          {selected && <IconCheck size={12} stroke={3} />}
                        </span>
                      </button>
                    )
                  })}
                </div>
              </div>
            ) : (
              <div className={classes.sourceBox} data-bordered="true">
                {library.loading ? (
                  <Loader size="sm" color="#d00b65" />
                ) : (
                  <>
                    <IconPhotoOff size={24} stroke={1.6} />
                    <p className={classes.sourceEmptyText}>
                      {library.error || <>{t('editorV2.form.image.noImagesLine1')}<br />{t('editorV2.form.image.noImagesLine2')}</>}
                    </p>
                    <button
                      type="button"
                      className={classes.pinkButton}
                      disabled={!library.website}
                      title={library.website ? undefined : t('editorV2.form.image.addWebsiteFirst')}
                      onClick={library.reload}
                    >
                      {t('editorV2.form.image.importImages')}
                    </button>
                  </>
                )}
              </div>
            )
          )}

          {tab === 'stock' && (
            <div className={classes.sourceBox}>
              {unsplashKey ? (
                <StockPhotoTab
                  unsplashKey={unsplashKey}
                  activeValue=""
                  query={stockQuery}
                  onQueryChange={setStockQuery}
                  lang={configuration?.ad?.language}
                  onSelect={(photo) => {
                    const url = photo?.urls?.raw || photo?.urls?.full || photo?.urls?.regular || photo?.urls?.small
                    if (url) adoptUrl(url, `unsplash-${photo?.id || 'photo'}.jpg`)
                  }}
                />
              ) : (
                <p className={classes.sourceEmptyText}>
                  {t('editorV2.form.common.unsplashMissing')}
                </p>
              )}
            </div>
          )}
        </div>
      </div>

      {removeText.outcome && <RemoveTextNotes removeText={removeText} />}
      {(localError || error) && <p className={classes.error}>{localError || error}</p>}

      {cropMode && target && (
        <ImageCropDialog
          opened={cropOpened}
          onClose={() => setCropOpened(false)}
          onApply={applyCrop}
          sourceUrl={sourceUrl}
          crop={media.crop}
          target={target}
          label={fieldLabel(field)}
        />
      )}
    </div>
  )
}
